// components/ChatListPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSession } from "../hooks/useSessionHook";
import BottomNav from "../components/BottomNav";
import TopHeader from "../components/TopHeader";
import supabase from "@/supabase";
import { formatDistanceToNow } from 'date-fns';
import { useRealtime } from "@/context/RealtimeContext";
import { useConversationList } from "@/hooks/use-conversation-list";
import { StoryRail } from "@/components/stories/StoryRail";

// Helper function to format dates safely
const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const formatPreview = (message: string | null | undefined) => {
  if (!message) return 'No messages yet';
  return message.length > 13 ? `${message.slice(0, 13)}...` : message;
};

export default function ChatListPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;
  const { friendships } = useRealtime();
  const {
    conversations,
    isLoading: conversationsLoading,
    error: conversationsError,
  } = useConversationList({
    userId: userId || '',
    enabled: !!userId,
  });
  const friendsLoading = false; // Now handled globally
  const friendsError = null; // Now handled globally

  const [friendProfiles, setFriendProfiles] = useState<Record<string, { display_name: string; username: string | null; avatar_url: string | null }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  
  // Fetch friend profiles
  useEffect(() => {
    if (!friendships.length) return;
    
    const fetchFriendProfiles = async () => {
      const friendIds = friendships.map(f => f.friend_id);
      
      const { data, error } = await supabase
        .rpc('get_accessible_profiles', { p_profile_ids: friendIds });
      
      if (error) {
        console.error('Error fetching friend profiles:', error);
        return;
      }
      
      const profilesMap: Record<string, { display_name: string; username: string | null; avatar_url: string | null }> = {};
      data.forEach((profile: { id: string; display_name: string; username: string | null; avatar_url: string | null }) => {
        profilesMap[profile.id] = {
          display_name: profile.display_name,
          username: profile.username,
          avatar_url: profile.avatar_url
        };
      });
      
      setFriendProfiles(profilesMap);
    };
    
    fetchFriendProfiles();
  }, [friendships]);
  
  useEffect(() => {
    if (!userId) return;
    
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Friend requests are now handled by useRealtimeFriends hook
        // No need to fetch them here
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [userId]);

  // Combine loading states
  const isLoadingData = isLoading || friendsLoading;
  const errorData = error || friendsError;
  const visibleConversations = activeTab === 'unread'
    ? conversations.filter((conversation) => conversation.awaiting_response)
    : conversations;
  
  return (
    <div className="jr-app-shell">
      <TopHeader />

      <main className="flex-1 overflow-y-auto pb-24">
        <StoryRail />

        <div className="jr-screen-pad pt-3">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'all' | 'unread')}>
              <TabsList className="jr-segmented">
              <TabsTrigger value="all" className="jr-segmented-trigger">All Chats</TabsTrigger>
              <TabsTrigger value="unread" className="jr-segmented-trigger flex items-center gap-1">
                Unread 
                {conversations.some((conversation) => conversation.awaiting_response) && (
                  <Badge variant="destructive" className="jr-badge-count ml-1 border-0">
                    {conversations.filter((conversation) => conversation.awaiting_response).length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
      
      {error && (
        <div className="jr-status-error mt-3 text-center">{error}</div>
      )}
      
      {(isLoadingData || conversationsLoading) && (
        <div className="py-8 text-center text-sm text-[var(--jr-text-muted)]">Loading...</div>
      )}
      
      {conversationsError && (
        <div className="jr-status-error mt-3 text-center">Failed to load chats: {conversationsError}</div>
      )}
      
      {errorData && (
        <div className="jr-status-error mt-3 text-center">{errorData}</div>
      )}
        </div>

        {!isLoading && !error && (
          <div className="jr-screen-pad pb-6 pt-4">
            <div className="jr-card overflow-hidden">
              {visibleConversations.length > 0 ? (
                visibleConversations.map((conversation) => (
                  <div 
                    key={conversation.id} 
                    className="flex cursor-pointer items-center border-b border-[color:var(--jr-border)] px-4 py-5 transition-colors last:border-b-0 hover:bg-[color:rgba(255,255,255,0.03)] md:py-6"
                    onClick={() => navigate(conversation.navigation_path)}
                  >
                    <Avatar className="jr-avatar-ring h-16 w-16 md:h-20 md:w-20">
                      {conversation.other_participant.avatar_url ? (
                        <AvatarImage
                          src={conversation.other_participant.avatar_url}
                          alt={conversation.other_participant.display_name || 'Unknown User'}
                        />
                      ) : (
                        <AvatarFallback className="bg-[color:var(--jr-surface-muted)] text-[color:var(--jr-accent)]">
                          {(conversation.other_participant.display_name || '?').charAt(0)}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 ml-4">
                      <p className="text-base font-semibold text-[var(--jr-text)] md:text-lg">
                        {conversation.other_participant.display_name || 'Unknown User'}
                      </p>
                      <p className="truncate text-sm text-[var(--jr-text-muted)] md:text-base">
                        {formatPreview(conversation.last_message?.content)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end ml-auto">
                      {conversation.last_message?.created_at && (
                      <p className="text-xs text-[var(--jr-text-muted)]">
                        {formatDate(conversation.last_message.created_at)}
                      </p>
                      )}
                      {conversation.unread_count > 0 && (
                        <Badge variant="destructive" className="jr-badge-count mt-1 border-0 p-0">
                          {conversation.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-sm text-[var(--jr-text-muted)]">
                  No chats yet. Start a new conversation!
                </div>
              )}
            </div>
        </div>
      )}
      </main>

      <BottomNav />
    </div>
  );
}
